import React, { useEffect, useState } from 'react';
import { sponsorsAPI } from '../utils/api';

/**
 * 스폰서 센터 — 광고주 셀프서비스 포털 (잉크 디자인).
 * 등록(pending) → 승인 후: 캠페인/콘텐츠/리포트 + 유료 서베이 생성·결과.
 * 질문 빌더 규칙: 한 줄에 한 질문, "질문 | 보기1;보기2;보기3" 형식이면 객관식.
 */
const pick = (o: any, ...keys: string[]): any => {
  for (const k of keys) if (o?.[k] !== undefined && o?.[k] !== null) return o[k];
  return undefined;
};

export default function SponsorPortal() {
  const [sponsor, setSponsor] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  // 등록 폼
  const [companyName, setCompanyName] = useState('');
  const [bizReg, setBizReg] = useState('');
  const [contactEmail, setContactEmail] = useState('');

  // 캠페인
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [campName, setCampName] = useState('');
  const [campTargets, setCampTargets] = useState('');
  const [reportOf, setReportOf] = useState<{ id: number; data: any } | null>(null);
  // 콘텐츠 폼 (캠페인별 토글)
  const [contentFor, setContentFor] = useState<number | null>(null);
  const [cTitle, setCTitle] = useState('');
  const [cBody, setCBody] = useState('');
  const [cCtaText, setCCtaText] = useState('');
  const [cCtaUrl, setCCtaUrl] = useState('');

  // 서베이
  const [surveys, setSurveys] = useState<any[]>([]);
  const [svTitle, setSvTitle] = useState('');
  const [svDesc, setSvDesc] = useState('');
  const [svReward, setSvReward] = useState('');
  const [svQuestions, setSvQuestions] = useState('');
  const [resultsOf, setResultsOf] = useState<{ id: number; data: any } | null>(null);

  const flash = (m: string) => { setMsg(m); setErr(''); setTimeout(() => setMsg(''), 4000); };
  const fail = (e: any, fallback: string) => { setErr(e?.response?.data?.error || fallback); setMsg(''); };

  const load = async () => {
    setLoading(true);
    try {
      const me = await sponsorsAPI.me();
      setSponsor(me.data.sponsor);
      if (me.data.sponsor?.status === 'approved') {
        const [c, s] = await Promise.all([sponsorsAPI.myCampaigns(), sponsorsAPI.mySurveys()]);
        setCampaigns(c.data.campaigns || []);
        setSurveys(s.data.surveys || []);
      }
    } catch {
      setSponsor(null); // 미등록
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const register = async () => {
    if (!companyName.trim()) { setErr('회사명을 입력해주세요.'); return; }
    try {
      await sponsorsAPI.register({ company_name: companyName.trim(), business_registration: bizReg.trim() || undefined, contact_email: contactEmail.trim() || undefined });
      flash('등록이 접수되었습니다. 관리자 승인을 기다려주세요.');
      await load();
    } catch (e: any) { fail(e, '등록에 실패했습니다.'); }
  };

  const createCampaign = async () => {
    if (!campName.trim()) { setErr('캠페인 이름을 입력해주세요.'); return; }
    try {
      const targets = campTargets.split(',').map(s => s.trim()).filter(Boolean);
      await sponsorsAPI.createCampaign({ name: campName.trim(), target_specialties: targets });
      setCampName(''); setCampTargets('');
      flash('캠페인이 생성되었습니다. 활성화하면 송출이 시작됩니다.');
      await load();
    } catch (e: any) { fail(e, '캠페인 생성에 실패했습니다.'); }
  };

  const toggleCampaign = async (c: any) => {
    const next = c.status === 'active' ? 'paused' : 'active';
    try { await sponsorsAPI.updateCampaign(c.id, { status: next }); flash(next === 'active' ? '캠페인이 활성화되었습니다.' : '캠페인이 일시정지되었습니다.'); await load(); }
    catch (e: any) { fail(e, '상태 변경에 실패했습니다.'); }
  };

  const addContent = async () => {
    if (contentFor == null) return;
    if (!cTitle.trim()) { setErr('콘텐츠 제목을 입력해주세요.'); return; }
    try {
      await sponsorsAPI.createContent(contentFor, { title: cTitle.trim(), body: cBody.trim() || undefined, cta_text: cCtaText.trim() || undefined, cta_url: cCtaUrl.trim() || undefined });
      setContentFor(null); setCTitle(''); setCBody(''); setCCtaText(''); setCCtaUrl('');
      flash('콘텐츠가 등록되었습니다. ("광고" 라벨과 함께 노출됩니다)');
      await load();
    } catch (e: any) { fail(e, '콘텐츠 등록에 실패했습니다.'); }
  };

  const openReport = async (id: number) => {
    try { const r = await sponsorsAPI.campaignReport(id); setReportOf({ id, data: r.data }); }
    catch (e: any) { fail(e, '리포트를 불러오지 못했습니다.'); }
  };

  const parseQuestionLines = (raw: string) => {
    const lines = raw.split('\n').map(l => l.trim()).filter(Boolean);
    return lines.map(line => {
      const [text, opts] = line.split('|').map(s => s.trim());
      if (opts) return { text, options: opts.split(';').map(o => o.trim()).filter(Boolean) };
      return { text };
    });
  };

  const createSurvey = async () => {
    if (!svTitle.trim() || !svQuestions.trim()) { setErr('서베이 제목과 질문을 입력해주세요.'); return; }
    try {
      await sponsorsAPI.createSurvey({
        title: svTitle.trim(),
        description: svDesc.trim() || undefined,
        reward_text: svReward.trim() || undefined,
        questions: parseQuestionLines(svQuestions),
        target_specialties: [],
      });
      setSvTitle(''); setSvDesc(''); setSvReward(''); setSvQuestions('');
      flash('서베이가 생성되었습니다. 활성화하면 검증 의사에게 노출됩니다.');
      await load();
    } catch (e: any) { fail(e, '서베이 생성에 실패했습니다.'); }
  };

  const toggleSurvey = async (s: any) => {
    const next = s.status === 'active' ? 'closed' : 'active';
    try { await sponsorsAPI.updateSurvey(s.id, { status: next }); flash(next === 'active' ? '서베이가 활성화되었습니다.' : '서베이가 종료되었습니다.'); await load(); }
    catch (e: any) { fail(e, '상태 변경에 실패했습니다.'); }
  };

  const openResults = async (id: number) => {
    try { const r = await sponsorsAPI.surveyResults(id); setResultsOf({ id, data: r.data }); }
    catch (e: any) { fail(e, '결과를 불러오지 못했습니다.'); }
  };

  if (loading) return <div className="py-16 text-center text-gray-400">불러오는 중…</div>;

  const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400';
  const btnCls = 'px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-700 text-sm disabled:opacity-50';
  const outBtnCls = 'px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm';

  return (
    <div className="max-w-3xl mx-auto">
      <h1 className="text-xl font-bold text-gray-900 mb-1">스폰서 센터</h1>
      <p className="text-sm text-gray-500 mb-6">검증된 의료진 대상 캠페인·서베이를 운영합니다. 모든 광고에는 라벨이 표기됩니다.</p>

      {msg && <div className="mb-4 border border-gray-200 rounded-lg p-3 text-sm text-gray-900 bg-gray-50">{msg}</div>}
      {err && <div className="mb-4 border border-red-200 rounded-lg p-3 text-sm text-red-600">{err}</div>}

      {!sponsor ? (
        <div className="border border-gray-200 rounded-xl p-6">
          <h2 className="font-semibold text-gray-900 mb-4">스폰서 등록</h2>
          <div className="space-y-3">
            <input value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="회사명 *" className={inputCls} />
            <input value={bizReg} onChange={e => setBizReg(e.target.value)} placeholder="사업자등록번호 (선택)" className={inputCls} />
            <input value={contactEmail} onChange={e => setContactEmail(e.target.value)} placeholder="담당자 이메일 (선택)" className={inputCls} />
            <button onClick={register} className={btnCls}>등록 신청</button>
          </div>
        </div>
      ) : sponsor.status !== 'approved' ? (
        <div className="border border-gray-200 rounded-xl p-6 text-center">
          <p className="text-gray-900 font-medium mb-1">{pick(sponsor, 'company_name', 'companyName')}</p>
          <p className="text-sm text-gray-500">
            {sponsor.status === 'suspended' ? '계정이 정지 상태입니다. 관리자에게 문의해주세요.' : '승인 대기 중입니다. 관리자 승인 후 캠페인을 만들 수 있습니다.'}
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          {/* 캠페인 */}
          <section>
            <h2 className="font-semibold text-gray-900 mb-3">광고 캠페인</h2>
            <div className="border border-gray-200 rounded-xl p-4 mb-3 space-y-2">
              <input value={campName} onChange={e => setCampName(e.target.value)} placeholder="새 캠페인 이름" className={inputCls} />
              <input value={campTargets} onChange={e => setCampTargets(e.target.value)} placeholder="타깃 전문과목 (쉼표 구분, 비우면 전체)" className={inputCls} />
              <button onClick={createCampaign} className={btnCls}>캠페인 생성</button>
            </div>
            {campaigns.length === 0 ? (
              <p className="text-sm text-gray-400">아직 캠페인이 없습니다.</p>
            ) : campaigns.map(c => (
              <div key={c.id} className="border border-gray-200 rounded-xl p-4 mb-2">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div>
                    <span className="font-medium text-gray-900">{c.name}</span>
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-xs border ${c.status === 'active' ? 'border-gray-900 text-gray-900' : 'border-gray-200 text-gray-500'}`}>
                      {c.status === 'active' ? '송출 중' : c.status === 'paused' ? '일시정지' : c.status === 'ended' ? '종료' : '초안'}
                    </span>
                    <span className="ml-3 text-xs text-gray-400">
                      콘텐츠 {Number(pick(c, 'content_count', 'contentCount')) || 0} · 노출 {Number(c.impressions) || 0} · 클릭 {Number(c.clicks) || 0}
                    </span>
                  </div>
                  <div className="space-x-2">
                    <button onClick={() => toggleCampaign(c)} className={outBtnCls}>{c.status === 'active' ? '일시정지' : '활성화'}</button>
                    <button onClick={() => setContentFor(contentFor === c.id ? null : c.id)} className={outBtnCls}>콘텐츠 추가</button>
                    <button onClick={() => openReport(c.id)} className={outBtnCls}>리포트</button>
                  </div>
                </div>
                {contentFor === c.id && (
                  <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                    <input value={cTitle} onChange={e => setCTitle(e.target.value)} placeholder="광고 제목 *" className={inputCls} />
                    <textarea value={cBody} onChange={e => setCBody(e.target.value)} placeholder="본문 (선택)" rows={2} className={inputCls} />
                    <div className="flex gap-2">
                      <input value={cCtaText} onChange={e => setCCtaText(e.target.value)} placeholder="버튼 문구" className={inputCls} />
                      <input value={cCtaUrl} onChange={e => setCCtaUrl(e.target.value)} placeholder="버튼 링크 URL" className={inputCls} />
                    </div>
                    <button onClick={addContent} className={btnCls}>등록</button>
                  </div>
                )}
                {reportOf?.id === c.id && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <table className="w-full text-sm">
                      <thead><tr className="text-left text-gray-500"><th className="py-1">콘텐츠</th><th>노출</th><th>클릭</th><th>CTR</th></tr></thead>
                      <tbody>
                        {reportOf!.data.contents.map((r: any) => (
                          <tr key={r.id} className="border-t border-gray-100">
                            <td className="py-1.5">{r.title}</td><td>{r.impressions}</td><td>{r.clicks}</td><td>{r.ctr}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </section>

          {/* 서베이 */}
          <section>
            <h2 className="font-semibold text-gray-900 mb-3">유료 서베이·자문단</h2>
            <div className="border border-gray-200 rounded-xl p-4 mb-3 space-y-2">
              <input value={svTitle} onChange={e => setSvTitle(e.target.value)} placeholder="서베이 제목 *" className={inputCls} />
              <input value={svDesc} onChange={e => setSvDesc(e.target.value)} placeholder="설명 (선택)" className={inputCls} />
              <input value={svReward} onChange={e => setSvReward(e.target.value)} placeholder="사례비 안내 (예: 응답 완료 시 상품권 3만원)" className={inputCls} />
              <textarea
                value={svQuestions}
                onChange={e => setSvQuestions(e.target.value)}
                placeholder={'질문 (한 줄에 하나)\n객관식: 질문 | 보기1;보기2;보기3\n주관식: 질문만 입력'}
                rows={4}
                className={inputCls}
              />
              <button onClick={createSurvey} className={btnCls}>서베이 생성</button>
            </div>
            {surveys.length === 0 ? (
              <p className="text-sm text-gray-400">아직 서베이가 없습니다.</p>
            ) : surveys.map(s => (
              <div key={s.id} className="border border-gray-200 rounded-xl p-4 mb-2">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div>
                    <span className="font-medium text-gray-900">{s.title}</span>
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-xs border ${s.status === 'active' ? 'border-gray-900 text-gray-900' : 'border-gray-200 text-gray-500'}`}>
                      {s.status === 'active' ? '진행 중' : s.status === 'closed' ? '종료' : '초안'}
                    </span>
                    <span className="ml-3 text-xs text-gray-400">응답 {Number(pick(s, 'response_count', 'responseCount')) || 0}건</span>
                  </div>
                  <div className="space-x-2">
                    <button onClick={() => toggleSurvey(s)} className={outBtnCls}>{s.status === 'active' ? '종료' : '활성화'}</button>
                    <button onClick={() => openResults(s.id)} className={outBtnCls}>결과</button>
                  </div>
                </div>
                {resultsOf?.id === s.id && (
                  <div className="mt-3 pt-3 border-t border-gray-100 space-y-3">
                    <p className="text-xs text-gray-400">총 {resultsOf!.data.responseCount}명 응답 (익명 집계)</p>
                    {resultsOf!.data.results.map((r: any, i: number) => (
                      <div key={i}>
                        <p className="text-sm font-medium text-gray-900 mb-1">{i + 1}. {r.question}</p>
                        {r.type === 'choice' ? (
                          <ul className="text-sm text-gray-600 space-y-0.5">
                            {Object.entries(r.counts).map(([opt, n]) => <li key={opt}>{opt}: {n as number}명</li>)}
                          </ul>
                        ) : (
                          <ul className="text-sm text-gray-600 list-disc pl-4 space-y-0.5">
                            {r.answers.map((a: string, j: number) => <li key={j}>{a}</li>)}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </section>
        </div>
      )}
    </div>
  );
}
